
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Checkbox } from '../../../components/ui/checkbox';
import { toast } from 'sonner';
import { api } from '../../../services/api';
import { legacyApi } from '../../../services/legacyApi';

interface DebitNoteModalProps {
    bill: any; // Using any for SupplierBill temporarily or import implementation
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const DebitNoteModal: React.FC<DebitNoteModalProps> = ({ bill, isOpen, onClose, onSuccess }) => {
    const [selectedItems, setSelectedItems] = useState<Record<number, number>>({});
    const [loading, setLoading] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [remarks, setRemarks] = useState('');
    const [details, setDetails] = useState<any[]>([]);

    React.useEffect(() => {
        if (isOpen && bill && bill.bill_id) {
            setSelectedItems({});
            setRemarks('');
            setLoadingDetails(true);
            legacyApi.purchasing.getBill(bill.bill_id)
                .then((fullBill) => {
                    setDetails(fullBill.details || []);
                })
                .catch((err) => {
                    console.error(err);
                    setDetails([]);
                    toast.error('Failed to load bill line items');
                })
                .finally(() => setLoadingDetails(false));
        }
    }, [isOpen, bill]);


    const handleToggle = (detailId: number, maxQty: number) => {
        setSelectedItems(prev => {
            const next = { ...prev };
            if (next[detailId] !== undefined) {
                delete next[detailId];
            } else {
                next[detailId] = maxQty;
            }
            return next;
        });
    };

    const handleQtyChange = (detailId: number, qty: number, maxQty: number) => {
        if (qty < 0) qty = 0;
        if (qty > maxQty) qty = maxQty;
        setSelectedItems(prev => ({ ...prev, [detailId]: qty }));
    };

    const handleSubmit = async () => {
        const returnItems = Object.entries(selectedItems).map(([id, qty]) => ({
            bill_detail_id: parseInt(id),
            quantity: qty
        }));

        if (returnItems.length === 0) {
            toast.error("Please select items to return");
            return;
        }

        try {
            setLoading(true);
            await api.returns.createPurchaseReturn(bill.bill_id, returnItems, remarks);
            toast.success("Debit Note Created Successfully");
            onSuccess();
            onClose();
        } catch (error: any) {
            toast.error(error.message || "Failed to create Debit Note");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>Create Debit Note (Return) - {bill.supplier_name}</DialogTitle>
                </DialogHeader>

                <div className="py-4">
                    <p className="text-sm text-muted-foreground mb-4">Select items to return for Bill #{bill.bill_id}</p>

                    <div className="border rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/50">
                                <tr>
                                    <th className="p-2 w-10"></th>
                                    <th className="p-2 text-left">Item</th>
                                    <th className="p-2 text-right">Billed Qty</th>
                                    <th className="p-2 text-right">Return Qty</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingDetails && (
                                    <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">Loading line items...</td></tr>
                                )}
                                {!loadingDetails && details.map((item: any) => (
                                    <tr key={item.bill_detail_id} className="border-t">
                                        <td className="p-2 text-center">
                                            <Checkbox
                                                checked={selectedItems[item.bill_detail_id] !== undefined}
                                                onCheckedChange={() => handleToggle(item.bill_detail_id, item.quantity)}
                                            />
                                        </td>
                                        <td className="p-2">{item.description || item.material_name}</td>
                                        <td className="p-2 text-right">{item.quantity}</td>
                                        <td className="p-2 text-right">
                                            <Input
                                                type="number"
                                                value={selectedItems[item.bill_detail_id] || ''}
                                                onChange={(e) => handleQtyChange(item.bill_detail_id, parseFloat(e.target.value), item.quantity)}
                                                disabled={selectedItems[item.bill_detail_id] === undefined}
                                                className="w-24 ml-auto h-8"
                                            />
                                        </td>
                                    </tr>
                                ))}
                                {!loadingDetails && details.length === 0 && (
                                    <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">No line items available to return.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-3">
                        <label className="mb-1 block text-sm font-medium">Remarks</label>
                        <Input
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            placeholder="Optional remarks for debit note"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={loading || loadingDetails}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={loading || loadingDetails || Object.keys(selectedItems).length === 0}>
                        {loading ? "Processing..." : "Create Debit Note"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
